import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Student } from '../types/student';
import { toast } from 'react-hot-toast';
import { logger } from '../lib/logger';

export function useVerification() {
  const [verifiedStudents, setVerifiedStudents] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const loadVerificationStatus = useCallback(async () => {
    try {
      setLoading(true);
      logger.info('Carregando status de verificação...');

      // Buscar todas as tabelas cad_
      const { data: tables, error: tablesError } = await supabase
        .from('tabelas_view')
        .select('table_name')
        .like('table_name', 'cad_%');

      if (tablesError) {
        throw tablesError;
      }

      if (!tables?.length) {
        logger.info('Nenhuma tabela de cadastro encontrada');
        return;
      }

      // Buscar registros verificados de cada tabela
      const verifiedIds = new Set<string>();
      
      for (const { table_name } of tables) {
        const { data, error } = await supabase
          .from(table_name)
          .select('id')
          .eq('verified', true);

        if (error) {
          logger.error(`Erro ao buscar verificações da tabela ${table_name}:`, error);
          continue;
        }

        data?.forEach(record => verifiedIds.add(record.id));
      }

      logger.info(`${verifiedIds.size} registros verificados encontrados`);
      setVerifiedStudents(verifiedIds);
    } catch (error) {
      logger.error('Erro ao carregar status de verificação:', error);
      toast.error('Erro ao carregar status de verificação');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVerificationStatus();
  }, [loadVerificationStatus]);

  const handleVerification = useCallback(async (student: Student) => {
    if (!student.originalTable) {
      toast.error('Tabela de origem não encontrada');
      return false;
    }

    if (!student.originalTable.startsWith('cad_')) {
      toast.error('Apenas registros de cadastro podem ser verificados');
      return false;
    }

    const newVerificationStatus = !verifiedStudents.has(student.id);
    
    try {
      logger.info('Atualizando status de verificação', {
        studentId: student.id,
        table: student.originalTable,
        status: newVerificationStatus
      });

      const { error } = await supabase
        .from(student.originalTable)
        .update({ verified: newVerificationStatus })
        .eq('id', student.id);

      if (error) throw error;

      setVerifiedStudents(prev => {
        const newSet = new Set(prev);
        if (newVerificationStatus) {
          newSet.add(student.id);
        } else {
          newSet.delete(student.id);
        }
        return newSet;
      });

      return true;
    } catch (error) {
      logger.error('Erro ao atualizar status de verificação:', error);
      toast.error('Erro ao atualizar status de verificação');
      return false;
    }
  }, [verifiedStudents]);

  const isVerified = useCallback((studentId: string) => {
    return verifiedStudents.has(studentId);
  }, [verifiedStudents]);

  return {
    verifiedStudents,
    loading,
    loadVerificationStatus,
    handleVerification,
    isVerified
  };
}